import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  addDoc,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db, auth } from "../firebaseConfig";
import * as XLSX from "xlsx";
import "../styles/List.css";

const PersonelYonetim = () => {
  const [formData, setFormData] = useState({
    ad: "",
    soyad: "",
    tc: "",
    gorev: "",
    not: "",
    tesis: "",
    girisTarihi: "",
    cikisTarihi: "",
  });

  const [personeller, setPersoneller] = useState([]);
  const [filters, setFilters] = useState({});
  const [showPassive, setShowPassive] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    fetchPersoneller();
  }, []);

  const fetchPersoneller = async () => {
    const snap = await getDocs(collection(db, "personeller"));
    const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    data.sort((a, b) => a.ad.localeCompare(b.ad, "tr", { sensitivity: "base" }));
    setPersoneller(data);
  };

  const validateTC = (tc) => /^[0-9]{11}$/.test(tc);

  const formatDate = (dateStr) => {
    const parts = dateStr.split(/[.\-\/]/);
    if (parts.length === 3) {
      let [a, b, c] = parts;
      if (a.length === 4) return `${c.padStart(2, "0")}.${b.padStart(2, "0")}.${a}`;
      if (c.length === 4) return `${a.padStart(2, "0")}.${b.padStart(2, "0")}.${c}`;
    }
    return dateStr;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const currentUser = auth.currentUser;
    const now = serverTimestamp();
    const tc = formData.tc.trim();

    if (!validateTC(tc)) return alert("TC kimlik numarası 11 haneli ve sadece rakam olmalıdır.");

    const q = query(collection(db, "personeller"), where("tc", "==", tc));
    const existing = await getDocs(q);

    if (!existing.empty) {
      const docRef = existing.docs[0].ref;
      const existingData = existing.docs[0].data();
      const tarih = existingData.tarih || {};
      const girisKeys = Object.keys(tarih).filter(k => k.startsWith("giris"));
      const cikisKeys = Object.keys(tarih).filter(k => k.startsWith("cikis"));

      if (girisKeys.length > cikisKeys.length) {
        if (!formData.cikisTarihi) return alert("Bu personel şu an çalışıyor. Çıkış tarihi girin.");
        tarih[`cikis${cikisKeys.length + 1}`] = formatDate(formData.cikisTarihi);
      } else {
        if (!formData.girisTarihi) return alert("Bu personel çıkmış görünüyor. Yeni giriş tarihi girin.");
        tarih[`giris${girisKeys.length + 1}`] = formatDate(formData.girisTarihi);
      }

      await updateDoc(docRef, {
        tarih,
        updateBy: currentUser.email,
        updateAt: now,
      });
      alert("Tarih güncellendi.");
    } else {
      if (!formData.ad || !formData.soyad || !formData.gorev || !formData.tesis || !formData.girisTarihi) {
        return alert("Yeni personel için tüm zorunlu alanları doldurun.");
      }
      const tarih = { giris1: formatDate(formData.girisTarihi) };
      if (formData.cikisTarihi) tarih.cikis1 = formatDate(formData.cikisTarihi);

      await addDoc(collection(db, "personeller"), {
        ad: formData.ad,
        soyad: formData.soyad,
        tc,
        gorev: formData.gorev,
        not: formData.not,
        tesis: formData.tesis,
        tarih,
        createdBy: currentUser.email,
        createdAt: now,
      });
      alert("Yeni personel eklendi.");
    }

    setFormData({ ad: "", soyad: "", tc: "", gorev: "", not: "", tesis: "", girisTarihi: "", cikisTarihi: "" });
    fetchPersoneller();
  };

  const handleEdit = (id, field, value) => {
    if (field === "tc") return;
    if (!value.trim()) return;
    setEditedData(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
    setIsDirty(true);
  };

  const handleBulkUpdate = async () => {
    const currentUser = auth.currentUser;
    const now = serverTimestamp();
    for (const id in editedData) {
      const ref = doc(db, "personeller", id);
      await updateDoc(ref, {
        ...editedData[id],
        updateBy: currentUser.email,
        updateAt: now,
      });
    }
    alert("Güncellemeler kaydedildi.");
    setIsDirty(false);
    setEditedData({});
    fetchPersoneller();
  };

  const isPersonelAktif = (tarih = {}) => {
    const girisler = Object.entries(tarih).filter(([k]) => k.startsWith("giris"));
    const cikislar = Object.entries(tarih).filter(([k]) => k.startsWith("cikis"));
    return girisler.length > cikislar.length;
  };

  const filteredPersoneller = personeller.filter(p => {
    const tarih = p.tarih || {};
    const isActive = isPersonelAktif(tarih);
    const girisler = Object.entries(tarih).filter(([k]) => k.startsWith("giris"));
    const latestGiris = girisler.length ? girisler.sort().slice(-1)[0][1] : "";
    const girisFilter = filters.girisTarihi;
    const tarihKarsiliyor = !girisFilter || (latestGiris && eval(`\"${latestGiris.split(".").reverse().join("-")}\" ${girisFilter}`));
    return (showPassive || isActive) &&
      tarihKarsiliyor &&
      Object.entries(filters).every(([key, val]) =>
        key === "girisTarihi" ||
        !val || (p[key]?.toLowerCase() || "").includes(val.toLowerCase())
      );
  });

  const paginated = filteredPersoneller.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const columns = ["ad", "soyad", "tc", "gorev", "tesis"];

  const exportToExcel = () => {
    const dataToExport = filteredPersoneller.map(p => {
      const tarih = p.tarih || {};
      const girisler = Object.entries(tarih).filter(([k]) => k.startsWith("giris")).sort();
      const cikislar = Object.entries(tarih).filter(([k]) => k.startsWith("cikis")).sort();
      return {
        AD: p.ad,
        SOYAD: p.soyad,
        TC: p.tc,
        GÖREV: p.gorev,
        TESİS: p.tesis,
        "GİRİŞ TARİHİ": girisler.map(([k, v]) => formatDate(v)).join(", "),
        "ÇIKIŞ TARİHİ": cikislar.map(([k, v]) => formatDate(v)).join(", ")
      };
    });
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Personeller");
    XLSX.writeFile(workbook, "personel_listesi.xlsx");
  };

  const toplamPersonel = filteredPersoneller.length;
  const tesisBazli = {};
  const gorevBazli = {};
  filteredPersoneller.forEach(p => {
    tesisBazli[p.tesis] = (tesisBazli[p.tesis] || 0) + 1;
    gorevBazli[p.gorev] = (gorevBazli[p.gorev] || 0) + 1;
  });

  return (
    <div className="list-container">
      <h2>Personel Ekle</h2>
      <form onSubmit={handleSubmit} className="form-inline">
        {["ad", "soyad", "tc", "gorev", "not", "tesis", "girisTarihi", "cikisTarihi"].map((field) => (
          <input
            key={field}
            name={field}
            value={formData[field] || ""}
            placeholder={field}
            onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
            required={field === "tc"}
          />
        ))}
        <button type="submit">Ekle</button>
      </form>

      <h2 style={{ marginTop: 30 }}>Personel Listesi</h2>
      <div>
        <button onClick={exportToExcel}>Excel İndir</button>
        <label style={{ marginLeft: 10 }}>
          <input type="checkbox" checked={showPassive} onChange={() => setShowPassive(!showPassive)} />
          Pasif personelleri göster
        </label>
      </div>

      <table className="narrow-table">
        <thead>
          <tr>
            {columns.map(col => (
              <th key={col}>
                {col.toUpperCase()}
                <br />
                <input
                  type="text"
                  placeholder="filtrele"
                  onChange={(e) => setFilters(prev => ({ ...prev, [col]: e.target.value }))}
                  style={{ width: "90%" }}
                />
              </th>
            ))}
            <th>Giriş Tarihi</th>
            <th>Çıkış Tarihi</th>
          </tr>
        </thead>
        <tbody>
          {paginated.map(p => {
            const tarih = p.tarih || {};
            const girisler = Object.entries(tarih).filter(([k]) => k.startsWith("giris"));
            const cikislar = Object.entries(tarih).filter(([k]) => k.startsWith("cikis"));
            return (
              <tr key={p.id}>
                {columns.map(col => (
                  <td key={col}>
                    <input
                      value={editedData[p.id]?.[col] ?? p[col] ?? ""}
                      onChange={(e) => handleEdit(p.id, col, e.target.value)}
                    />
                  </td>
                ))}
                <td>{girisler.map(([k, v]) => <div key={k}>{formatDate(v)}</div>)}</td>
                <td>{cikislar.map(([k, v]) => <div key={k}>{formatDate(v)}</div>)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div>
        Sayfa:
        {[...Array(Math.ceil(filteredPersoneller.length / itemsPerPage)).keys()].map(p => (
          <button key={p} onClick={() => setCurrentPage(p + 1)}>{p + 1}</button>
        ))}
      </div>
      <div style={{ marginTop: 10 }}>
        <button onClick={handleBulkUpdate} disabled={!isDirty}>GÜNCELLE</button>
      </div>

      <div className="dashboard-summary" style={{ marginTop: 30, padding: 15, background: "#f5f5f5", borderRadius: 8 }}>
        <h3>Personel Durumu</h3>
        <p><strong>Toplam personel:</strong> {toplamPersonel}</p>
        <h4>Tesis Bazında Dağılım</h4>
        <ul>
          {Object.entries(tesisBazli).map(([tesis, sayi]) => <li key={tesis}>{tesis}: {sayi}</li>)}
        </ul>
        <h4>Görev Bazında Dağılım</h4>
        <ul>
          {Object.entries(gorevBazli).map(([gorev, sayi]) => <li key={gorev}>{gorev}: {sayi}</li>)}
        </ul>
      </div>
    </div>
  );
};

export default PersonelYonetim;
